/**
 * Per-feed MLS attribution config. Keyed by the EXACT `mls_feeds.feed_id`
 * value (verified against prod 2026-08-07: 'TriangleMLS', 'TriadMLS',
 * 'FMLS', 'GeorgiaMLS', 'CanopyMLS') — a typo here silently drops legally
 * required text, which is why keys are an explicit allowlist:
 *
 *   - feed HAS an entry  -> the whole attribution block renders from it;
 *   - feed has NO entry  -> no block renders at all and the miss is reported
 *     at error level (ACR-23 req 10). No default config, no substring
 *     matching, no generic fallback line: a wrong or partial attribution is
 *     a violation, absence is a Sentry alert we act on.
 *
 * Lines 3-5 of the block (sourceLine + mark, copyright, disclaimers) come
 * exclusively from here — the component carries no per-feed conditionals.
 * Each feed's mark is the CDN logo the payload already carries
 * (`mlsIdx.logoUrl`, i.e. /images/logos/mls/{feed_id}.png), derived from the
 * feed id rather than configured per feed.
 *
 * Each entry cites the rule it satisfies. Any change to `strings.ts` or the
 * flags here must be reflected in the compliance matrix row for that MLS
 * (docs: ACR-23) in the same PR.
 */
import { MLS_STRINGS } from './strings'

export type FeedAttributionConfig = {
  /** Rule ref, for reviewers */
  ruleRef: string
  /**
   * Appended directly after the office name, punctuation included (Triad
   * 18.2 (K): ", licensed in NC"). null for feeds whose rules add nothing.
   */
  officeSuffix: string | null
  /** Line 3: the feed's attribution sentence, with the mark inline after it */
  sourceLine: string
  /** Line 4. null for feeds whose rules require no copyright notice. */
  copyright: string | null
  /** Line 5, one paragraph each. Empty for feeds that owe no disclaimer. */
  disclaimers: readonly string[]
  /** Extra line shown only when the listing is sold (per-feed sold rules) */
  soldNotice?: string
  /**
   * Marks the feed as an MLS GRID redistributor, which owes the Rule 24
   * "as of {DataTimestamp}" sentence verbatim. Rendering needs no flag — the
   * disclaimer is just config — so this exists as the config-integrity
   * tripwire asserted in config.test.ts: a GRID feed whose disclaimers lose
   * the Rule 24 text fails CI instead of shipping silently.
   */
  gridTimestampDisclaimer?: boolean
  /**
   * Renders the feed's mark as a photo-corner badge on listing-card
   * thumbnails. Triad 18.2 (F)/(G): the feed's icon must sit adjacent to
   * the property info on thumbnails — photo-corner placement mirrors
   * Zillow's live Triad display; formal Triad confirmation PENDING (ACR-18),
   * revisit if Triad reads "adjacent" as requiring proximity to the
   * beds/baths line. Consumers: the photo `Figure` in
   * ListingsGrid/ListingCard.tsx (grid + static cards, incl. the calculator
   * page), via the shared MlsMark component. CARD SURFACES ONLY — the
   * detail-page block (MlsAttribution.tsx) renders the payload's CDN logo
   * and reads none of the mark fields below.
   */
  thumbnailIcon?: boolean
  /**
   * Render size for the photo-corner badge (thumbnailIcon surfaces): the
   * asset's intrinsic (1x) pixel size, because the half-size render was
   * illegible on the photo. Omitted -> MlsMark falls back to
   * markWidth/markHeight.
   */
  thumbnailMarkWidth?: number
  thumbnailMarkHeight?: number
  /**
   * MLS-supplied mark for CARD surfaces (Triad 18.2 (F)). Self-hosted @2x
   * asset with two render sizes: markWidth/markHeight is half its pixel
   * dimensions at 2x density (24w option per design, ACR-18) and the badge
   * uses thumbnailMarkWidth/Height, its intrinsic 48x22 (1x). Explicit width
   * AND height: no CLS, and the icon's visual size is deliberate, not an
   * aspect-ratio accident. Alt text is decorative — compliance text NEVER
   * rides on an image.
   */
  markSrc?: string
  markWidth?: number
  markHeight?: number
}

export const ATTRIBUTION_CONFIGS: Readonly<
  Record<string, FeedAttributionConfig>
> = Object.freeze({
  TriangleMLS: {
    ruleRef: 'Doorify MLS 12.4, 17.10',
    officeSuffix: MLS_STRINGS.TriangleMLS.officeSuffix,
    sourceLine: MLS_STRINGS.TriangleMLS.sourceLine,
    copyright: MLS_STRINGS.TriangleMLS.copyright,
    disclaimers: MLS_STRINGS.TriangleMLS.disclaimers,
  },
  CanopyMLS: {
    ruleRef: 'MLS GRID Rules 22-24',
    officeSuffix: MLS_STRINGS.CanopyMLS.officeSuffix,
    sourceLine: MLS_STRINGS.CanopyMLS.sourceLine,
    copyright: MLS_STRINGS.CanopyMLS.copyright,
    disclaimers: MLS_STRINGS.CanopyMLS.disclaimers,
    soldNotice: MLS_STRINGS.CanopyMLS.soldNotice,
    gridTimestampDisclaimer: true,
    markSrc: '/mls-marks/mls-grid.png',
    markWidth: 24,
    markHeight: 16,
  },
  TriadMLS: {
    ruleRef: 'Triad MLS 18.2 (F)(H)(I)(K)',
    officeSuffix: MLS_STRINGS.TriadMLS.officeSuffix,
    sourceLine: MLS_STRINGS.TriadMLS.sourceLine,
    copyright: MLS_STRINGS.TriadMLS.copyright,
    disclaimers: MLS_STRINGS.TriadMLS.disclaimers,
    thumbnailIcon: true,
    thumbnailMarkWidth: 48,
    thumbnailMarkHeight: 22,
    markSrc: '/mls-marks/triad-mls.png',
    markWidth: 24,
    markHeight: 11,
  },
  FMLS: {
    ruleRef: 'FMLS Rules 11.9, 11.10, 13.7(e)(2)',
    officeSuffix: MLS_STRINGS.FMLS.officeSuffix,
    sourceLine: MLS_STRINGS.FMLS.sourceLine,
    copyright: MLS_STRINGS.FMLS.copyright,
    disclaimers: MLS_STRINGS.FMLS.disclaimers,
  },
  GeorgiaMLS: {
    ruleRef: 'GAMLS §12(e)(j)',
    officeSuffix: MLS_STRINGS.GeorgiaMLS.officeSuffix,
    sourceLine: MLS_STRINGS.GeorgiaMLS.sourceLine,
    copyright: MLS_STRINGS.GeorgiaMLS.copyright,
    disclaimers: MLS_STRINGS.GeorgiaMLS.disclaimers,
  },
})
